import { useForm } from "@tellescope/forms";
import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeMutation,
  useTellescopeQuery,
  useUsersMap,
} from "@tellescope/tanstack-query";
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
  EmojiPicker,
  Input,
  Label,
  Textarea,
} from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { ComponentProps, ReactNode, useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { UserSearchCombobox } from "../UserSearchCombobox";

export interface EditTeamChannelDialogProps extends ComponentProps<typeof Dialog> {
  chatroomId?: string;
  children: ReactNode;
  onSave?: (chatroom: TellescopeModelFromName<"chat_rooms">) => void;
  onCancel?: () => void;
  autoFocusField?: "title" | "userIds" | "description";
}

export const EditTeamChannelDialog = ({
  chatroomId,
  children,
  onSave,
  onCancel,
  open: providedOpen,
  onOpenChange: providedOnOpenChange,
  defaultOpen,
  autoFocusField = "title",
  ...rest
}: EditTeamChannelDialogProps) => {
  const { t } = useTranslation("team-chat");
  const { currentUser } = useAuth();
  const [openState, setOpenState] = useState(defaultOpen ?? providedOpen);
  const open = providedOpen || openState;
  const onOpenChange = useCallback(
    (open: boolean) => {
      setOpenState(open);
      providedOnOpenChange?.(open);
    },
    [providedOnOpenChange]
  );
  const existingChatroomQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getOne",
    request: chatroomId ?? "",
    enabled: !!chatroomId,
  });

  const createChatroomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "createOne",

    onSuccess: (res) => {
      onSave?.(res);
    },
  });

  const updateChatroomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "updateOne",

    onSuccess: (res) => {
      onSave?.(res);
    },
  });

  const initialUserIds = useMemo<string[]>(() => {
    const userIdSet = new Set<string>(existingChatroomQuery.data?.userIds ?? []);
    if (currentUser.id && existingChatroomQuery.data?.creator === currentUser.id) {
      userIdSet.add(currentUser.id);
    }

    return Array.from(userIdSet);
  }, [currentUser.id, existingChatroomQuery.data?.creator, existingChatroomQuery.data?.userIds]);

  const form = useForm({
    fields: {
      title: {
        type: "text",
        required: true,
        initialValue: existingChatroomQuery.data?.title,
      },
      emoji: {
        type: "emoji",
        initialValue: existingChatroomQuery.data?.emoji,
      },
      userIds: {
        type: "multiselect",
        initialValue: initialUserIds,
      },
      description: {
        type: "textarea",
        required: false,
        initialValue: existingChatroomQuery.data?.description,
      },
    },
    validateOn: "change",
    onSubmit: async (values) => {
      if (chatroomId) {
        await updateChatroomMutation.mutateAsync({
          id: chatroomId,
          ...values,
          _options: {
            replaceObjectFields: true,
          },
        });
      } else {
        await createChatroomMutation.mutateAsync({ ...values, type: "internal", enduserIds: [] });
      }
      onOpenChange(false);
    },
  });

  const usersMap = useUsersMap();

  return (
    <Dialog
      {...rest}
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          form.resetForm();
        }
        onOpenChange(open);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent key={existingChatroomQuery.data?.updatedAt?.toISOString() ?? "new"}>
        <DialogTitle>{chatroomId ? t("Edit Team Channel") : t("New Team Channel")}</DialogTitle>
        <form {...form.getFormProps()} className="space-y-4">
          <div className="space-y-2">
            <Label>{t("Channel Name")}</Label>
            <div className="flex gap-1">
              <EmojiPicker
                {...form.getCustomFieldProps("emoji")}
                triggerButton={
                  <Button
                    variant="outline"
                    className={cn("transition-none transition-opacity", form.values.emoji ? "px-2" : "px-3")}
                  >
                    {form.values.emoji ?? "☺"}
                  </Button>
                }
              />
              <Input
                {...form.getFieldProps("title")}
                placeholder={t("Enter a team channel name...")}
                autoFocus={autoFocusField === "title"}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("Participants")}</Label>
            <div className="gap-1 flex flex-wrap">
              {form.values.userIds.map((userId) => {
                const user = usersMap[userId];

                if (!user) return null;

                return (
                  <Badge
                    key={userId}
                    variant="secondary"
                    onClick={() => {
                      if (userId === currentUser.id) return;
                      form.setFieldValue(
                        "userIds",
                        form.values.userIds.filter((id) => id !== userId)
                      );
                    }}
                    className={userId !== currentUser.id ? "cursor-pointer" : undefined}
                  >
                    {formatName(user)}
                    {userId !== currentUser.id && <X />}
                  </Badge>
                );
              })}
            </div>
            <UserSearchCombobox
              multiple
              value={form.values.userIds.flatMap((userId) => {
                const user = usersMap[userId];
                return user ? [user] : [];
              })}
              onChange={(users) =>
                form.setFieldValue(
                  "userIds",
                  users.map(({ id }) => id)
                )
              }
              placeholder={t("Select users...")}
              clearable={false}
              customFilter={(_, users) => users.filter((user) => user.value !== currentUser.id)}
              alwaysFilter
              open={autoFocusField === "userIds"}
            >
              <Button variant="outline" className="w-full justify-start" type="button">
                <Plus />
                {t("Add Participants")}
              </Button>
            </UserSearchCombobox>
          </div>
          <div className="space-y-2">
            <Label>
              <Trans ns="team-chat">
                Description <span className="text-muted-foreground font-normal">(optional)</span>
              </Trans>
            </Label>
            <Textarea
              {...form.getFieldProps("description")}
              placeholder={t("Enter a description...")}
              autoFocus={autoFocusField === "description"}
            />
          </div>
        </form>
        <DialogFooter>
          <DialogClose onClick={onCancel} asChild>
            <Button variant="outline">{t("Cancel")}</Button>
          </DialogClose>
          <Button
            onClick={() => form.submitForm()}
            disabled={!form.isValid || !form.isDirty}
            loading={updateChatroomMutation.isPending || createChatroomMutation.isPending}
          >
            {chatroomId ? t("Save Channel") : t("Create Channel")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
